import React, { useState, useCallback, useEffect } from 'react';
import axios from '../../axiosInstance';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import FilterTags1 from '../../components/FilterTags1';
import Pagination from '../../components/Pagination';
import SortByAlphaIcon from '@mui/icons-material/SortByAlpha'; // For Alphabetical Sort
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import ArticleIcon from '@mui/icons-material/Article';
import Facets from '../../components/Facets';
import defaultThumbnail from '../../assets/eBraryIcon.png';
import SearchBox_ebrary from '../../components/SearchBox_ebrary_journals';
import { useTheme } from '@mui/material/styles';
import ReactGA from 'react-ga';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';


import {
  Typography,
  Box,
  CircularProgress,
  Snackbar,
  Modal,
  Paper,
  IconButton, 
  Tabs,
  Tab,
  useMediaQuery,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { styled } from '@mui/system';
import PropTypes from 'prop-types';



// Register the required components
ChartJS.register(ArcElement, Tooltip, Legend);  



// Function to convert search results to CSV format
const convertResultsToCSV = (results) => {
  const headers = ['Title', 'Publisher', 'ISSN', 'Total Articles', 'Impact Factor', 'H-Index', 'i10 Index', 'Open Access', 'Source'];
  const rows = results.map(result => [
    `"${result.title}"`,
    `"${result.publisher}"`,
    `"${result.issn}"`,
    `"${result.totalWorks}"`,
    `"${result.impactFactor}"`,
    `"${result.hIndex}"`,
    `"${result.i10Index}"`,
    `"${result.isOA}"`,
    `"${result.source}"`,
  ]);

  const csvContent = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
  return csvContent;
};

// Function to handle download
const handleDownload = (results) => {
  const csvData = convertResultsToCSV(results || []);
  if (!results || results.length === 0) {
    alert('No data available to download.');
    return;
  }

  const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = 'search_results.csv';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};



const EJournals = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const queryFromUrl = new URLSearchParams(location.search).get('query') || '';
  const filtersFromUrl = Object.fromEntries(
    new URLSearchParams(location.search).entries()
  );

  const isMobile = useMediaQuery('(max-width:768px)'); // Adjust breakpoint as needed
  const [tabValue, setTabValue] = useState(0);
  
  const [selectedDatabase, setSelectedDatabase] = useState(
    location.state?.selectedDatabase || 'All Databases'
  );

  const extractedFilters = {};
  Object.keys(filtersFromUrl).forEach(param => {
    if (param.startsWith('f.')) {
      const facetName = param.replace('f.', '');
      const value = filtersFromUrl[param].split(',')[0]; // Extract value
      extractedFilters[facetName] = value;
    }
  });
  
  const [query, setQuery] = useState(queryFromUrl);

  const handleDatabaseChange = (event) => {
    setSelectedDatabase(event.target.value);
  };

  const [results, setResults] = useState([]);
  const [filteredResults, setFilteredResults] = useState([]);
  const [totalResults, setTotalResults] = useState(0);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false); // State to control modal open/close
  const [selectedArticleId, setSelectedArticleId] = useState(null); // State to store selected article ID
  const [isExpanded, setIsExpanded] = useState(false);  // State for toggling "Read More"
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [viewMode, setViewMode] = useState('list');
  const [sortOrder, setSortOrder] = useState('relevance');
  const [selectedSort, setSelectedSort] = useState({ field: 'score', direction: 'DESC' });

  // Add state for facets and selected filters
const [facets, setFacets] = useState({});
const [selectedFilters, setSelectedFilters] = useState({});

  const [openAccessPercentage, setOpenAccessPercentage] = useState(0);
  const [doajPercentage, setDoajPercentage] = useState(0);
  const [indiaPercentage, setIndiaPercentage] = useState(0);

const [openAddFilterDialog, setOpenAddFilterDialog] = useState(false);



  const theme = useTheme(); // Access the theme

    // Check if user is logged in using localStorage
    const userLoggedIn = localStorage.getItem('token') ? true : false;
    
  const resultsPerPage = 20; // Define results per page


 
  const [showSortMenu, setShowSortMenu] = useState(false);


  const handleTabChange = (event, newValue) => {
    setTabValue(newValue); // Change active tab
  };

  
  const truncateText = (text, maxLength) => {
    if (!text || text.length <= maxLength) return text;
    return text.slice(0, maxLength) + '...';
  };

    // Example of `fetchResults` with properly closed braces and commas
    const fetchResults = useCallback(async (searchQuery, pageNumber = 1, filters = {}, sort = 'cited_by_count:desc') => {
      setLoading(true);
      setError(null);
    
      try {
        const filterParams = Object.keys(filters)
          .map((key) => `${key}:${filters[key]}`)
          .join(',');
    
    
        const response = await axios.get('https://api.openalex.org/sources', {
          params: {
            filter: `type:source-types/journal,display_name.search:${searchQuery}${filterParams ? `,${filterParams}` : ''}`,
            sort,
            page: pageNumber,
          },
        });
    
        const items = response.data.results;
    
        setResults(
          items.map((item) => ({
            id: item.id.replace('https://openalex.org/', ''),
            title: item.display_name,
            publisher: item.host_organization_name,
            issn: item.issn_l || 'N/A',
            totalWorks: item.works_count || 'N/A',
            impactFactor: item["summary_stats"]?.["2yr_mean_citedness"] || 'N/A',
            hIndex: item["summary_stats"]?.h_index || 'N/A',
            i10Index: item["summary_stats"]?.i10_index || 'N/A',
            isOA: item.is_oa ? 'Yes' : 'No',
            subjects: item.x_concepts?.map((concept) => concept.display_name).join(', ') || 'N/A',
            source: 'Open Alex',
          }))
        );
        setTotalResults(response.data.meta.count);
      } catch (err) {
        setError('Failed to fetch results.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    }, []);
    


    const fetchFacets = useCallback(async (searchQuery, filters = {}) => {
      try {
        const filterParams = Object.entries(filters)
          .map(([key, value]) => `${key}:${value}`)
          .join(',');
    
        const response = await axios.get('https://api.openalex.org/sources', {
          params: {
            filter: `type:source-types/journal,display_name.search:${searchQuery}${
              filterParams ? `,${filterParams}` : ''
            }`,
            group_by: 'facet_name', // Replace with actual facet group if applicable
          },
        });
    
        const facetsData = response.data.group_by || [];
        setFacets(facetsData); // Update facets state
      } catch (err) {
        console.error('Error fetching facets:', err);
      }
    }, []);


    

    const fetchGroupedData = useCallback(async (activeFilters = {}) => {
      try {
        const filterParams = Object.entries(activeFilters)
          .map(([key, value]) => `${key}:${value}`)
          .join(',');
    
        const responseOA = await axios.get('https://api.openalex.org/sources', {
          params: {
            group_by: 'is_oa',
            filter: `type:source-types/journal${filterParams ? `,${filterParams}` : ''}`,
          },
        });
    
        const responseDOAJ = await axios.get('https://api.openalex.org/sources', {
          params: {
            group_by: 'is_in_doaj',
            filter: `type:source-types/journal${filterParams ? `,${filterParams}` : ''}`,
          },
        });
    
        const responseIndia = await axios.get('https://api.openalex.org/sources', {
          params: {
            group_by: 'country_code',
            filter: `type:source-types/journal,country_code:IN${filterParams ? `,${filterParams}` : ''}`,
          },
        });
    
        const openAccessGroups = responseOA.data.group_by || [];
        const doajGroups = responseDOAJ.data.group_by || [];
        const indiaGroup = responseIndia.data.group_by?.[0] || { count: 0 };
    
        const totalResults = responseOA.data.meta.count || 0;
        const openAccessCount = openAccessGroups.find((group) => group.key === "1")?.count || 0;
        const doajCount = doajGroups.find((group) => group.key === "1")?.count || 0;
        const indiaCount = indiaGroup.count || 0;
    
        setTotalResults(totalResults);
        setOpenAccessPercentage((openAccessCount / totalResults) * 100);
        setDoajPercentage((doajCount / totalResults) * 100);
        setIndiaPercentage((indiaCount / totalResults) * 100); // New state for India
      } catch (error) {
        console.error('Error fetching grouped data:', error);
      }
    }, []);



    const handleFilterClick = (filterKey, filterValue) => {
      const searchParams = new URLSearchParams(location.search);
    
      // Get current filters from the URL
      const existingFilter = searchParams.get('filter') || '';
      const filterSet = new Set(existingFilter.split(',').filter(Boolean)); // Convert to a Set to avoid duplicates
    
      // Add or replace the specific filter
      filterSet.add(`${filterKey}:${filterValue}`); // Ensure the correct key:value pair
    
      // Update the filter parameter
      searchParams.set('filter', Array.from(filterSet).join(','));
    
      // Navigate with updated filters
      navigate(`/EJournals?${searchParams.toString()}`);
    
      // Update the state
      const activeFilters = {};
      filterSet.forEach((filter) => {
        const [key, value] = filter.split(':');
        activeFilters[key] = value;
      });
    
      // Trigger fetch operations with updated filters
      fetchResults(query, page, activeFilters);
      fetchGroupedData(activeFilters);
      fetchFacets(query, activeFilters);
    };
    
    


     // Function to remove a filter
     const removeFilter = (facetName) => {
      const searchParams = new URLSearchParams(location.search);
    
      // Get current filters from the URL
      const existingFilter = searchParams.get('filter') || '';
      const filterSet = new Set(existingFilter.split(',').filter(Boolean)); // Convert to a Set
    
      // Remove the filter
      filterSet.forEach((filter) => {
        if (filter.startsWith(`${facetName}:`)) {
          filterSet.delete(filter);
        }
      });
    
      // Update the filter parameter
      if (filterSet.size > 0) {
        searchParams.set('filter', Array.from(filterSet).join(','));
      } else {
        searchParams.delete('filter'); // Remove filter parameter if no filters remain
      }
    
      // Navigate with updated filters
      navigate(`/EJournals?${searchParams.toString()}`);
    
      // Update the state
      const activeFilters = {};
      filterSet.forEach((filter) => {
        const [key, value] = filter.split(':');
        activeFilters[key] = value;
      });
    
      fetchResults(query, page, activeFilters);
      fetchGroupedData(activeFilters);
      fetchFacets(query, activeFilters);
    };



    
    const handleAddFilter = (facetName, value) => {
      const updatedFilters = { ...selectedFilters, [facetName]: value };
      setSelectedFilters(updatedFilters);
    
      // Update URL
      const searchParams = new URLSearchParams(location.search);
      Object.keys(updatedFilters).forEach((key) => {
        searchParams.set(`f.${key.toLowerCase()}`, updatedFilters[key]);
      });
    
      navigate(`/EJournals?${searchParams.toString()}`);
    };


      const calculateRange = () => {
        const startIndex = (page - 1) * resultsPerPage + 1;
        const endIndex = Math.min(page * resultsPerPage, totalResults);
        return `${startIndex}-${endIndex}`;
      };

      const handlePageChange = (newPage) => {
        setPage(newPage);
      
        const searchParams = new URLSearchParams();
        searchParams.set('query', query);

        searchParams.set('spc.page', newPage);
      
        navigate(`/EJournals?${searchParams.toString()}`);
      
        // Fetch results for the new page
        fetchResults(query, newPage);
      };


      const handleSortChange = (newSortOrder) => {
        let sortQueryParam = '';
      
        if (newSortOrder === 'alphabetical') {
          sortQueryParam = 'display_name'; // Alphabetical sort
        } else if (newSortOrder === 'citations') {
          sortQueryParam = 'cited_by_count:desc'; // Sort by citations
        } else if (newSortOrder === 'impact_factor') {
          sortQueryParam = 'summary_stats.2yr_mean_citedness:desc'; // Sort by impact factor
        } else if (newSortOrder === 'Total Citations') {
          sortQueryParam = ''; // Sort by impact factor
        }
      
        // Update URL
        
        const searchParams = new URLSearchParams(location.search);
        searchParams.set('sort', sortQueryParam);
      
        Object.keys(selectedFilters).forEach((key) => {
          searchParams.set(`f.${key.toLowerCase()}`, selectedFilters[key]);
        });
      
        navigate(`/EJournals?${searchParams.toString()}`);
      
        // Fetch results immediately
        fetchResults(query, page, selectedFilters, sortQueryParam);
      
        setSortOrder(newSortOrder); // Update state
      };


// Refetch results when query or page changes
useEffect(() => {
  const queryFromUrl = new URLSearchParams(location.search).get('query') || '';
  const sortFromUrl = new URLSearchParams(location.search).get('sort') || 'cited_by_count:desc'; // Default sort
  const filtersFromUrl = Object.fromEntries(new URLSearchParams(location.search).entries());
  const pageFromUrl = parseInt(new URLSearchParams(location.search).get('spc.page')) || 1;

  

  // Extract filters and pass them to fetch functions
  const activeFilters = {};
  if (filtersFromUrl.filter) {
    const filterEntries = filtersFromUrl.filter.split(',').map((filter) => filter.split(':'));
    filterEntries.forEach(([key, value]) => {
      activeFilters[key] = value;
    });
  }
  setQuery(queryFromUrl);
  setPage(pageFromUrl);
  setSelectedFilters(activeFilters);
  setSortOrder(sortFromUrl);

  // Fetch results and facets based on query and filters
  fetchResults(queryFromUrl, pageFromUrl, activeFilters, sortFromUrl);
  fetchFacets(queryFromUrl, activeFilters);
}, [location.search]);


useEffect(() => {
  if (totalResults > 0) {
    setTotalPages(Math.ceil(totalResults / resultsPerPage));
  }
}, [totalResults, resultsPerPage]);


useEffect(() => {
  const filtersFromUrl = Object.fromEntries(new URLSearchParams(location.search).entries());
  const activeFilters = {};

  if (filtersFromUrl.filter) {
    const filterEntries = filtersFromUrl.filter.split(',').map((filter) => filter.split(':'));
    filterEntries.forEach(([key, value]) => {
      activeFilters[key] = value;
    });
  }

  setSelectedFilters(activeFilters);
  fetchGroupedData(activeFilters);
}, [location.search, fetchGroupedData]);

useEffect(() => {
  fetchResults(query, page, selectedFilters); // Fetch results when query changes
  fetchFacets(query, selectedFilters); // Fetch facets when query changes
}, [query, selectedFilters, page]);

useEffect(() => {
  if (query) {
    fetchFacets(query, selectedFilters);
  }
}, [query, selectedFilters, fetchFacets]);

useEffect(() => {
  if (location.state) {
    const { query: initialQuery, selectedDatabase } = location.state;
    setQuery(initialQuery || ''); // Set the query from navigation state
    setSelectedDatabase(selectedDatabase || 'eJournals'); // Default database
  } else {
    const queryFromUrl = new URLSearchParams(location.search).get('query') || '';
    setQuery(queryFromUrl); // Fallback to query from URL
  }
}, [location.state, location.search]);





  


  const SearchResultsWrapper = styled(Box)(({ theme }) => ({
    width: '60%', // Default for larger screens
    paddingLeft: theme.spacing(2),
    minHeight: '500px',
    [theme.breakpoints.down('md')]: {
      width: '100%', // Full width on medium screens or smaller
      paddingLeft: 0,
    },
  }));

  const YouTubeResultsWrapper = styled(Box)(({ theme }) => ({
    width: '40%', // Default for larger screens
    paddingLeft: theme.spacing(2),
    minHeight: '500px',
    [theme.breakpoints.down('md')]: {
      width: '100%', // Full width on medium screens or smaller
      paddingLeft: 0,
    },
  }));


  // Function to handle title click
  const onTitleClick = (result) => {
    trackButtonClick(result.title);
    if (!userLoggedIn) {
      localStorage.setItem('redirectAfterLogin', JSON.stringify(result)); // Store clicked article details
      return;
    }
  else
    {
      navigate(`/ejournals/${result.id}`);
    }
  };

const trackButtonClick = (buttonName) => {
  console.log(`Button clicked: ${buttonName}`);
  localStorage.setItem("lastButtonClicked", buttonName);
  ReactGA.event({
    category: 'User',
    action: 'Clicked Button or Link',
    label: buttonName,
  });
};

const [articleSource, setArticleSource] = useState(''); // Track article source type



  return (
  <div>
  <main>
    

    {loading && <CircularProgress />}
      {error && (
        <Snackbar open autoHideDuration={6000}>
          <div></div>
        </Snackbar>
      )}      

    <SearchBox_ebrary />
    <br></br>
    
      {isMobile ? (
          <>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
              sx={{ marginBottom: '16px' }}
            >
              <Tab label="Search Results" />
              <Tab label="Search Filters" />
            </Tabs>

            {tabValue === 0 && (
              <Box sx={{ width: '100%', alignItems: 'center', }}>
                <SearchResultsWrapper>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      width: '100%'
                    }}
                  >
                    <Typography
                      variant="h7"
                      color="primary.main"
                      sx={{ fontWeight: 'bold' }}
                    >
                      {`Showing ${calculateRange()} of ${totalResults.toLocaleString()} journals`}
                    </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <IconButton
                      onClick={() => handleSortChange('citations')}
                      title="Sort by Citations"
                    >
                      <FormatQuoteIcon style={{ marginRight: '8px' }} />
                    </IconButton>

                    <IconButton
                      onClick={() => handleSortChange('alphabetical')}
                      title="Sort Alphabetically"
                    >
                      <SortByAlphaIcon />
                    </IconButton>

                    <IconButton
                      onClick={() => handleSortChange('impact_factor')}
                      title="Sort by Impact Factor"
                    >
                      <ArticleIcon style={{ marginRight: '8px' }} />
                    </IconButton>

                    <IconButton
                      onClick={() => handleSortChange('')}
                      title="Sort by Citation Count"
                    >
                      <TrendingUpIcon style={{ marginRight: '8px' }} />
                    </IconButton>

                    <IconButton onClick={() => handleDownload(filteredResults)}>
                      <DownloadIcon />
                    </IconButton>

                  </Box>
                  

                  {/* Render Search Results */}
                  {results.map((result) => (
                    <Paper
                      key={result.title}
                      sx={{
                        padding: '1px',
                        marginBottom: '12px',
                        borderRadius: '8px',
                        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                        transition: 'all 0.3s ease-in-out',
                        width: '100%',
                        border: '1px solid transparent',
                        '&:hover': {
                          backgroundColor: '#f0f8ff',
                          borderColor: '#007bff',
                        },
                      }}
                    >
                      <Box display="flex" alignItems="center">
                        <img
                          src={result.thumbnail || defaultThumbnail}
                          alt="Thumbnail"
                          style={{
                            width: '80px',
                            height: '100px',
                            marginRight: '1px',
                          }}
                        />
                        <Box>
                          <Typography
                            variant="h6"
                            sx={{
                              cursor: 'pointer',
                              color: 'primary.main',
                            }}
                            onClick={() => onTitleClick(result)}
                          >
                            {result.title}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {`Authors: ${result.authors}`}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {`Year: ${result.year}`} | {`Published by: ${
                              result.publisher || 'N/A'
                            }`}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {`Subject: ${result.subject || 'N/A'}`}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  ))}
                  <Pagination
                    page={page}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                  />
                </SearchResultsWrapper>
              </Box>
            )}

            {tabValue === 1 && (

            <Box sx={{ 
              width: '98%',
              marginLeft: '5px',
              alignItems: 'center',
              }}
            >
                <YouTubeResultsWrapper>
                <Paper 
                  elevation={0} 
                  sx={{ 
                    marginTop: '16px', 
                    backgroundColor: 'transparent',
                    }}>

                    <Paper
                      sx={{
                        padding: '16px',
                        marginBottom: '12px',
                        borderRadius: '8px',
                        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                        transition: 'all 0.3s ease-in-out', // Smooth transition
                        border: '1px solid transparent', // Default border
                        '&:hover': {
                          backgroundColor: '#f0f8ff', // Change to your desired hover color
                          borderColor: '#007bff', // Optional: Change border color on hover
                        },
                      }}
                    >
                  <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold', marginBottom: '16px' }}>
                    {totalResults.toLocaleString()} Journals
                  </Typography>
                  </Paper>

                  <Paper
                      sx={{
                        padding: '16px',
                        marginBottom: '12px',
                        borderRadius: '8px',
                        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                        transition: 'all 0.3s ease-in-out', // Smooth transition
                        border: '1px solid transparent', // Default border
                        '&:hover': {
                          backgroundColor: '#f0f8ff', // Change to your desired hover color
                          borderColor: '#007bff', // Optional: Change border color on hover
                        },
                      }}
                    >
                          <a
                        href="#"
                        onClick={() => handleFilterClick('is_oa', 'true')}
                        style={{ textDecoration: 'none', color: 'inherit' }}
                      >
                  <Box sx={{ marginBottom: '16px' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        Fully Open Access
                    </Typography>
                    <Typography variant="h4" color="primary">
                      {openAccessPercentage.toFixed(1)}%
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {Math.round((openAccessPercentage / 100) * totalResults).toLocaleString()} journals
                    </Typography>
                  </Box>
                  </a>
                  </Paper>

                  <Paper
                      sx={{
                        padding: '16px',
                        marginBottom: '12px',
                        borderRadius: '8px',
                        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                        transition: 'all 0.3s ease-in-out', // Smooth transition
                        border: '1px solid transparent', // Default border
                        '&:hover': {
                          backgroundColor: '#f0f8ff', // Change to your desired hover color
                          borderColor: '#007bff', // Optional: Change border color on hover
                        },
                      }}
                    >
                      <a
                        href="#"
                        onClick={() => handleFilterClick('is_in_doaj', 'true')}
                        style={{ textDecoration: 'none', color: 'inherit' }}
                      >
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        Indexed in DOAJ
                    </Typography>
                    <Typography variant="h4" color="primary">
                      {doajPercentage.toFixed(2)}%
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {Math.round((doajPercentage / 100) * totalResults).toLocaleString()} journals
                    </Typography>
                  </Box>
                  </a>
                  </Paper>

                  <Paper
                sx={{
                  padding: '16px',
                  marginBottom: '12px',
                  borderRadius: '8px',
                  boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                  transition: 'all 0.3s ease-in-out',
                  border: '1px solid transparent',
                  '&:hover': {
                    backgroundColor: '#f0f8ff',
                    borderColor: '#007bff',
                  },
                }}
              >
                <a
                  href="#"
                  onClick={() => handleFilterClick('country_code', 'IN')}
                  style={{ textDecoration: 'none', color: 'inherit' }}
                >
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      Published in India
                    </Typography>
                    <Typography variant="h4" color="primary">
                      {indiaPercentage.toFixed(2)}%
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {Math.round((indiaPercentage / 100) * totalResults).toLocaleString()} journals
                    </Typography>
                  </Box>
                </a>
              </Paper>

                </Paper>
                </YouTubeResultsWrapper>
              </Box>
            )}
          </>
        ) : (


      <Box className="main-content" display="flex">
        {/* Left Section: Search Results */}
        <SearchResultsWrapper>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                whiteSpace: 'nowrap',
                paddingBottom: '2px',
                paddingTop: '20px',
                gap: '2px',
                  }}
              >
            <Typography variant="h7" color="primary.main" sx={{ fontWeight: 'bold' }}>
              {`Showing ${calculateRange()} of ${totalResults.toLocaleString()} journals`}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        
        <IconButton
          onClick={() => handleSortChange('citations')}
          title="Sort by Citations"
        >
          <FormatQuoteIcon style={{ marginRight: '8px' }} />
        </IconButton>

        <IconButton
          onClick={() => handleSortChange('alphabetical')}
          title="Sort Alphabetically"
        >
          <SortByAlphaIcon />
        </IconButton>

        <IconButton
          onClick={() => handleSortChange('impact_factor')}
          title="Sort by Impact Factor"
        >
          <ArticleIcon style={{ marginRight: '8px' }} />
        </IconButton>

        <IconButton
          onClick={() => handleSortChange('')}
          title="Sort by Citation Count"
        >
          <TrendingUpIcon style={{ marginRight: '8px' }} />
        </IconButton>

        <IconButton onClick={() => handleDownload(results)}>
        <DownloadIcon />
        </IconButton>

      </Box>
    </Box>

    {/* Render Search Results */}
    {results.map((result) => (
      <Paper
        key={result.title}
        sx={{
          padding: '16px',
          marginBottom: '12px',
          marginTop: '12px',
          borderRadius: '8px',
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
          transition: 'all 0.3s ease-in-out', // Smooth transition
          border: '1px solid transparent', // Default border
          '&:hover': {
            backgroundColor: '#f0f8ff', // Change to your desired hover color
            borderColor: '#007bff', // Optional: Change border color on hover
          },
        }}
      >
        <Box display="flex" alignItems="center">
          <img
            src={result.thumbnail || defaultThumbnail}
            alt="Thumbnail"
            style={{ 
              width: '80px', 
              height: '80px', 
              marginRight: '16px',
            }}
          />
          <Box>
          <Typography
              variant="h6"
              sx={{
                cursor: 'pointer', // Ensures the pointer cursor is shown
                color: 'primary.main', // Use Material-UI's primary color
              }}
              onClick={() => onTitleClick(result)}
            >
              {result.title}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {`ISSN: ${result.issn}`} | {`Published by: ${result.publisher || 'N/A'}`}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {`Subjects Coverage: ${result.subjects || 'N/A'}`}
            </Typography>
          </Box>
        </Box>
      </Paper>
    ))}
    <Pagination page={page} totalPages={totalPages} onPageChange={handlePageChange} />
    
  </SearchResultsWrapper>

  <YouTubeResultsWrapper>
  <Paper 
    elevation={0} 
    sx={{ 
      marginTop: '76px', 
      backgroundColor: 'transparent',
      }}>

                    <Paper
                      sx={{
                        padding: '16px',
                        marginBottom: '12px',
                        borderRadius: '8px',
                        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                        transition: 'all 0.3s ease-in-out', // Smooth transition
                        border: '1px solid transparent', // Default border
                        '&:hover': {
                          backgroundColor: '#f0f8ff', // Change to your desired hover color
                          borderColor: '#007bff', // Optional: Change border color on hover
                        },
                      }}
                    >

    <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold', marginBottom: '16px' }}>
      {totalResults.toLocaleString()} Journals
    </Typography>
    </Paper>

      <Box
        sx={{
          width: '98%',
          margin: '0 auto', // Centers the box horizontally
          textAlign: 'center', // Centers text inside the box
          flexWrap: 'wrap',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="h6" sx={{ marginBottom: '8px' }}>
          
        </Typography>
        <FilterTags1
        selectedFilters={selectedFilters}
        handleAddFilter={handleAddFilter}
        removeFilter={removeFilter}
        facets={facets} // Updated facets based on the current search query
      />
      </Box>



    <Paper
        sx={{
          padding: '16px',
          marginBottom: '12px',
          borderRadius: '8px',
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
          transition: 'all 0.3s ease-in-out', // Smooth transition
          border: '1px solid transparent', // Default border
          '&:hover': {
            backgroundColor: '#f0f8ff', // Change to your desired hover color
            borderColor: '#007bff', // Optional: Change border color on hover
          },
        }}
      >
        <a
          href="#"
          onClick={() => handleFilterClick('is_oa', 'true')}
          style={{ textDecoration: 'none', color: 'inherit' }}
        >
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
          Fully Open Access
        </Typography>
        <Typography variant="h4" color="primary">
          {openAccessPercentage.toFixed(1)}%
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {Math.round((openAccessPercentage / 100) * totalResults).toLocaleString()} journals
        </Typography>
      </Box>
      </a>
    </Paper>

    <Paper
        sx={{
          padding: '16px',
          marginBottom: '12px',
          borderRadius: '8px',
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
          transition: 'all 0.3s ease-in-out', // Smooth transition
          border: '1px solid transparent', // Default border
          '&:hover': {
            backgroundColor: '#f0f8ff', // Change to your desired hover color
            borderColor: '#007bff', // Optional: Change border color on hover
          },
        }}
      >
      <a
         href="#"
         onClick={() => handleFilterClick('is_in_doaj', 'true')}
         style={{ textDecoration: 'none', color: 'inherit' }}
      >
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
          Indexed in DOAJ
      </Typography>
      <Typography variant="h4" color="primary">
        {doajPercentage.toFixed(2)}%
      </Typography>
      <Typography variant="body2" color="textSecondary">
        {Math.round((doajPercentage / 100) * totalResults).toLocaleString()} journals
      </Typography>
    </Box>
    </a>
    </Paper>

    <Paper
      sx={{
        padding: '16px',
        marginBottom: '12px',
        borderRadius: '8px',
        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
        transition: 'all 0.3s ease-in-out',
        border: '1px solid transparent',
        '&:hover': {
          backgroundColor: '#f0f8ff',
          borderColor: '#007bff',
        },
      }}
    >
  <a
    href="#"
    onClick={() => handleFilterClick('country_code', 'IN')}
    style={{ textDecoration: 'none', color: 'inherit' }}
  >
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
        Published in India
      </Typography>
      <Typography variant="h4" color="primary">
        {indiaPercentage.toFixed(2)}%
      </Typography>
      <Typography variant="body2" color="textSecondary">
        {Math.round((indiaPercentage / 100) * totalResults).toLocaleString()} journals
      </Typography>
    </Box>
  </a>
</Paper>

  </Paper>
</YouTubeResultsWrapper>




        </Box>
)}
    </main>
    </div>
  );
};

EJournals.propTypes = {
  query: PropTypes.string.isRequired,
};

export default EJournals;
